import Analytics from '@withlocals/withlocals-analytics';
import { Translator } from 'Translator';
import LanguagesList from 'components/LanguagesList';
import { AddFluxHOCProps, addFlux } from 'components/wrappers/WithFlux';
import { getFilter } from 'data/filters';
import cloneDeep from 'lodash.clonedeep';
import routeToSearchURL from 'modules/homescreen/helpers/routeToSearchURL';
import React, { PureComponent } from 'react';

import Popover from '../../../../components/Popover';

const CSS = require('./LocalSpeaksPopover.less');

type LocalSpeaksPopoverProps = AddFluxHOCProps & {
  initialSelectedLanguages: number[];
  onClose: ({
    source,
  }: {
    source: 'apply button' | 'outside click' | 'escape button';
  }) => void;
};

interface LocalSpeaksPopoverState {
  selectedLanguages: number[];
}

class LocalSpeaksPopover extends PureComponent<
  LocalSpeaksPopoverProps & AddFluxHOCProps,
  LocalSpeaksPopoverState
> {
  state = {
    selectedLanguages: cloneDeep(this.props.initialSelectedLanguages) || [],
    aggregations: this.props.flux.stores.SearchStore.getAggregations(
      this.props.flux.stores.FiltersStore.getFilters(),
    ),
  };

  onLanguageClick = (value: string) => (event) => {
    const currentLanguages = [...this.state.selectedLanguages];
    const clickedLanguage = parseInt(event.currentTarget.value, 10);

    if (!currentLanguages.includes(clickedLanguage)) {
      currentLanguages.push(clickedLanguage);
    } else {
      const index = currentLanguages.indexOf(clickedLanguage);
      currentLanguages.splice(index, 1);
    }

    this.setState({ selectedLanguages: currentLanguages });
  };

  isPristine() {
    const { selectedLanguages } = this.state;
    const { initialSelectedLanguages } = this.props;
    if (selectedLanguages.length !== initialSelectedLanguages.length) {
      return false;
    }

    return selectedLanguages.every((selectedLanguage) =>
      initialSelectedLanguages.some(
        (initialSelectedLanguage) =>
          initialSelectedLanguage === selectedLanguage,
      ),
    );
  }

  onApply =
    (source: 'apply button' | 'outside click' | 'escape button') => (e) => {
      e?.stopPropagation();

      const langs = this.state.selectedLanguages;

      if (this.isPristine()) {
        this.props.onClose({ source });
        return;
      }

      const langFilter = getFilter('languages.items[]');
      langFilter.subOptions = langFilter.subOptions.filter((subOption) =>
        langs.includes(subOption.id),
      );

      routeToSearchURL({ filtersToAdd: [langFilter] });

      Analytics.track('Search page', 'Filter local speaks', {
        selectedLanguagesArr: langs,
        selectedLanguagesObj: Object.assign(
          {},
          ...langs.map((lang) => ({ [lang]: true })),
        ),
      });

      this.props.onClose({ source });
    };

  onClear = () => {
    this.setState({ selectedLanguages: [] });
  };

  render() {
    return (
      <Popover
        show
        underNavbar
        name="localSpeaksPopover"
        className={CSS.Popover}
        onClose={this.onApply('escape button')}
        onOutSideClick={this.onApply('outside click')}
      >
        <LanguagesList
          selectedLanguages={this.state.selectedLanguages}
          onLanguageClick={this.onLanguageClick}
          aggregations={this.state.aggregations}
        />
        <div className={CSS.ButtonContainer}>
          <button className={CSS.ClearButton} onClick={this.onClear}>
            {Translator.translate('Common.clear', {}, true, 'Clear')}
          </button>
          <button
            className={CSS.ApplyButton}
            onClick={this.onApply('apply button')}
          >
            {Translator.translate('Common.Apply', {}, true, 'Apply')}
          </button>
        </div>
      </Popover>
    );
  }
}

export default addFlux(LocalSpeaksPopover);
